import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/store/auth.store';

// const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3001/api/v1';
const BASE_URL = import.meta.env.VITE_API_URL;

export const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15_000,
});

// ── Request interceptor — attach Bearer token ─────────────────────
// [SECURITY] NHA audit E2: /auth/refresh intentionally does NOT receive an
// Authorization header. It authenticates via the refresh token in the request
// body, not a Bearer token. Injecting the (possibly expired) access token here
// would cause the gateway to reject the request with 401 before it can issue
// a new pair. The bypass is explicit and deliberate — not an omission.
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const isRefreshCall = config.url?.includes('/auth/refresh');
  if (!isRefreshCall) {
    const token = useAuthStore.getState().accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// ── Response interceptor — handle 401 with refresh ───────────────
let isRefreshing = false;
let failedQueue: Array<{ resolve: (v: any) => void; reject: (e: any) => void }> = [];

function processQueue(error: any, token: string | null = null) {
  failedQueue.forEach(p => (error ? p.reject(error) : p.resolve(token)));
  failedQueue = [];
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    if (error.response?.status === 401 && !original._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          original.headers.Authorization = `Bearer ${token}`;
          return api(original);
        });
      }

      original._retry = true;
      isRefreshing    = true;

      const refreshToken = useAuthStore.getState().refreshToken;

      if (!refreshToken) {
        // No refresh token means the user is not authenticated yet (e.g. a 401
        // from /auth/mfa/setup/confirm for a wrong TOTP code). Do NOT logout or
        // redirect — just propagate so the caller's catch block handles it.
        isRefreshing = false;
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post(`${BASE_URL}/auth/refresh`, { refreshToken });
        useAuthStore.getState().updateTokens(data.accessToken, data.refreshToken);
        processQueue(null, data.accessToken);
        original.headers.Authorization = `Bearer ${data.accessToken}`;
        return api(original);
      } catch (refreshError) {
        processQueue(refreshError, null);
        useAuthStore.getState().logout();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// ── Typed API service functions ───────────────────────────────────
export const authApi = {
  login:            (email: string, password: string, tenantSlug?: string) =>
    api.post('/auth/login', { email, password, tenantSlug }),
  mfaVerify:        (token: string, tempKey: string) =>
    api.post('/auth/mfa/verify', { token, tempKey }),
  mfaRecovery:      (code: string, tempKey: string) =>
    api.post('/auth/mfa/recovery', { code, tempKey }),
  mfaSetupInit:     (tempKey: string) =>
    api.post('/auth/mfa/setup/init', { tempKey }),
  mfaSetupConfirm:  (tempKey: string, token: string) =>
    api.post('/auth/mfa/setup/confirm', { tempKey, token }),
  mfaSetup:         () =>
    api.post('/auth/mfa/setup'),
  mfaConfirm:       (token: string) =>
    api.post('/auth/mfa/confirm', { token }),
  refresh:          (refreshToken: string) =>
    api.post('/auth/refresh', { refreshToken }),
  logout:           () =>
    api.post('/auth/logout'),
  sessions:         () =>
    api.get('/auth/sessions'),
  revokeSession:    (sid: string) =>
    api.delete(`/auth/sessions/${sid}`),
};

export const onboardingApi = {
  start:     () =>
    api.post('/onboarding/start'),
  step1:     (tenantId: string, token: string, data: object) =>
    api.put(`/onboarding/${tenantId}/step1`, data, { headers: { 'x-onboarding-token': token } }),
  step2:     (tenantId: string, token: string, filenames: string[]) =>
    api.put(`/onboarding/${tenantId}/step2`, { filenames }, { headers: { 'x-onboarding-token': token } }),
  step3:     (tenantId: string, token: string, data: object) =>
    api.put(`/onboarding/${tenantId}/step3`, data, { headers: { 'x-onboarding-token': token } }),
  provision: (tenantId: string, token: string, data: object) =>
    api.post(`/onboarding/${tenantId}/provision`, data, { headers: { 'x-onboarding-token': token } }),
  status:    (tenantId: string, token: string) =>
    api.get(`/onboarding/${tenantId}/status`, { headers: { 'x-onboarding-token': token } }),
  pincode:   (pin: string) =>
    api.get(`/pincode/${pin}`),
};

export const planApi = {
  list:         () => api.get('/admin/plans'),
  create:       (data: object) => api.post('/admin/plans', data),
  update:       (slug: string, data: object) => api.patch(`/admin/plans/${slug}`, data),
};

export const upgradeRequestApi = {
  list:         (params?: { status?: string; page?: number }) =>
    api.get('/admin/upgrade-requests', { params }),
  process:      (id: string, action: 'approve' | 'reject', admin_note?: string) =>
    api.patch(`/admin/upgrade-requests/${id}`, { action, admin_note }),
};

export const subscriptionApi = {
  mine:          () => api.get('/subscription'),
  requestUpgrade:(to_plan: string, note?: string) =>
    api.post('/subscription/upgrade-request', { to_plan, note }),
  cancelUpgrade: () => api.delete('/subscription/upgrade-request'),
};

export const adminApi = {
  stats:               () => api.get('/admin/stats'),
  hospitals:           (params?: object) => api.get('/admin/hospitals', { params }),
  hospital:            (id: string) => api.get(`/admin/hospitals/${id}`),
  updateHospital:      (id: string, data: object) => api.patch(`/admin/hospitals/${id}`, data),
  setStatus:           (id: string, status: string) => api.patch(`/admin/hospitals/${id}/status`, { status }),
  modules:             (id: string) => api.get(`/admin/hospitals/${id}/modules`),
  toggleModule:        (id: string, key: string, enabled: boolean) =>
    api.patch(`/admin/hospitals/${id}/modules`, { key, enabled }),
  updateLimits:        (id: string, data: object) => api.patch(`/admin/hospitals/${id}/limits`, data),
  users:               (id: string) => api.get(`/admin/hospitals/${id}/users`),
  addUser:             (id: string, data: object) => api.post(`/admin/hospitals/${id}/users`, data),
  updateUser:          (id: string, userId: string, data: object) =>
    api.patch(`/admin/hospitals/${id}/users/${userId}`, data),
  setUserStatus:       (id: string, userId: string, isActive: boolean) =>
    api.patch(`/admin/hospitals/${id}/users/${userId}/status`, { isActive }),
  resetUserPassword:   (id: string, userId: string, password: string) =>
    api.post(`/admin/hospitals/${id}/users/${userId}/reset-password`, { password }),
  debugAuth:           (email: string, tenantSlug?: string) =>
    api.get('/admin/debug/auth', { params: { email, ...(tenantSlug ? { tenantSlug } : {}) } }),
};

export const appointmentsApi = {
  list:    (params?: { date?: string; patient_id?: string; doctor_id?: string; status?: string }) =>
    api.get('/appointments', { params }),
  book:    (data: object) => api.post('/appointments', data),
  update:  (id: string, data: object) => api.patch(`/appointments/${id}`, data),
  checkIn: (id: string, data?: { chief_complaint?: string }) =>
    api.post(`/appointments/${id}/check-in`, data ?? {}),
};

export const patientApi = {
  list:         (params?: { search?: string; page?: number; limit?: number; include_archived?: boolean }) =>
    api.get('/patients', { params }),
  get:          (id: string)   => api.get(`/patients/${id}`),
  create:       (data: object) => api.post('/patients', data),
  update:       (id: string, data: object) => api.patch(`/patients/${id}`, data),
  encounters:   (id: string)   => api.get(`/patients/${id}/encounters`),
  prevVitals:   (id: string, exclude?: string) =>
    api.get(`/patients/${id}/prev-vitals`, { params: exclude ? { exclude } : {} }),
  updateDemographics: (id: string, data: object) => api.patch(`/patients/${id}/demographics`, data),
  archive:      (id: string, data: { archive: boolean; reason?: string }) =>
    api.patch(`/patients/${id}/archive`, data),
  auditLog:     (id: string, params?: { limit?: number }) =>
    api.get(`/patients/${id}/audit-log`, { params }),
};

export const brandingApi = {
  get:          ()                                  => api.get('/settings/branding'),
  update:       (data: object)                      => api.patch('/settings/branding', data),
  uploadLogo:   (dataUrl: string)                   => api.post('/settings/branding/logo', { dataUrl }),
  uploadHeader: (type: string, dataUrl: string)     => api.post(`/settings/branding/header/${type}`, { dataUrl }),
  removeHeader: (type: string)                      => api.delete(`/settings/branding/header/${type}`),
};

export const adminBrandingApi = {
  get:        (hospitalId: string)               => api.get(`/admin/hospitals/${hospitalId}/branding`),
  update:     (hospitalId: string, data: object) => api.patch(`/admin/hospitals/${hospitalId}/branding`, data),
  uploadLogo: (hospitalId: string, dataUrl: string) =>
    api.post(`/admin/hospitals/${hospitalId}/branding/logo`, { dataUrl }),
};

export const supportApi = {
  list:     (params?: { status?: string; page?: number }) =>
    api.get('/support/tickets', { params }),
  get:      (id: string) =>
    api.get(`/support/tickets/${id}`),
  create:   (data: object) =>
    api.post('/support/tickets', data),
  // admin
  adminList: (params?: { status?: string; priority?: string; tenant_id?: string; page?: number }) =>
    api.get('/admin/support/tickets', { params }),
  adminReply: (id: string, data: { admin_reply: string; status: string }) =>
    api.patch(`/admin/support/tickets/${id}`, data),
};

export const dashboardApi = {
  executive:    () => api.get('/dashboard/executive'),
  owner:        () => api.get('/dashboard/owner'),
  lab:          () => api.get('/dashboard/lab'),
  radiology:    () => api.get('/dashboard/radiology'),
  doctor:       () => api.get('/dashboard/doctor'),
  nurse:        () => api.get('/dashboard/nurse'),
  pharmacy:     () => api.get('/dashboard/pharmacy'),
  receptionist: () => api.get('/dashboard/receptionist'),
  billing:      () => api.get('/dashboard/billing'),
  hospitalAdmin:() => api.get('/dashboard/hospital-admin'),
};

export const billingMasterApi = {
  list:        (params?: { category?: string; active?: string }) =>
    api.get('/billing-master', { params }),
  doctors:     () => api.get('/billing-master/doctors'),
  catalogue:   (type: 'pathology' | 'bed' | 'radiology') =>
    api.get('/billing-master/catalogue', { params: { type } }),
  create:      (data: object) => api.post('/billing-master', data),
  batchCreate: (data: { items: object[] }) => api.post('/billing-master/batch', data),
  update:      (id: string, data: object) => api.patch(`/billing-master/${id}`, data),
  remove:      (id: string) => api.delete(`/billing-master/${id}`),
};

export const settingsApi = {
  getOpd:   () => api.get('/settings/opd'),
  patchOpd: (data: { free_followup_days: number }) => api.patch('/settings/opd', data),
};

export const profileApi = {
  get:            () => api.get('/users/me'),
  update:         (data: { name?: string; mobile?: string }) => api.patch('/users/me', data),
  changePassword: (data: { current_password: string; new_password: string }) =>
    api.post('/users/me/password', data),
};

export const opdApi = {
  queue:           (params?: { date?: string; doctor_id?: string }) =>
    api.get('/opd/queue', { params }),
  getEncounter:    (id: string) => api.get(`/opd/encounters/${id}`),
  createEncounter: (data: object) => api.post('/opd/encounters', data),
  updateEncounter: (id: string, data: object) => api.patch(`/opd/encounters/${id}`, data),
  updateVitals:    (id: string, data: object) => api.patch(`/opd/encounters/${id}/vitals`, data),
  slots:           (doctorId: string, date?: string) =>
    api.get(`/opd/slots/${doctorId}`, { params: { date } }),
  doctors:         () => api.get('/opd/doctors'),
};

export const abdmApi = {
  // ABHA — Aadhaar OTP flow
  initiateAadhaar: (aadhaar: string) =>
    api.post<{ txnId: string; mobileHint: string }>('/abdm/abha/initiate-aadhaar', { aadhaar }),
  verifyAadhaarOtp: (txnId: string, otp: string) =>
    api.post<{ txnId: string; verified: boolean }>('/abdm/abha/verify-aadhaar-otp', { txnId, otp }),
  createAbha: (txnId: string, patientId?: string) =>
    api.post<{ abhaNumber: string; abhaAddress: string; name: string; linked: boolean }>(
      '/abdm/abha/create',
      patientId ? { txnId, patientId } : { txnId }
    ),

  // ABHA — Mobile OTP flow
  initiateMobile: (mobile: string) =>
    api.post<{ txnId: string }>('/abdm/abha/initiate-mobile', { mobile }),
  verifyMobileOtp: (txnId: string, otp: string) =>
    api.post<{ txnId: string; verified: boolean }>('/abdm/abha/verify-mobile-otp', { txnId, otp }),

  // ABHA — QR scan
  verifyQr: (token: string) =>
    api.post<{ demographics: Record<string, string> }>('/abdm/abha/verify-qr', { token }),

  // ABHA — manual link
  linkAbha: (patientId: string, abhaNumber: string, abhaAddress?: string) =>
    api.patch(`/abdm/abha/link/${patientId}`, { abhaNumber, abhaAddress }),

  // HIE — consent + data push
  requestConsent:    (encounterId: string) =>
    api.post(`/abdm/hie/consent-request/${encounterId}`),
  pushHealthData:    (encounterId: string) =>
    api.post(`/abdm/hie/push/${encounterId}`),
  hieHistory:        (patientId: string) =>
    api.get(`/abdm/hie/history/${patientId}`),

  // PM-JAY
  pmjayVerify:       (data: { patientId: string; beneficiaryId: string; admissionId?: string; scheme?: string }) =>
    api.post('/abdm/pmjay/verify', data),
  pmjayPreAuth:      (data: { verificationId: string; packageCode: string; packageName: string; treatmentDetails?: Record<string, unknown> }) =>
    api.post('/abdm/pmjay/pre-auth', data),
  pmjayHistory:      (patientId: string) =>
    api.get(`/abdm/pmjay/verifications/${patientId}`),
};
